import React, { useEffect, useState, useCallback } from "react";
import { api, formatError } from "../../lib/api";
import { toast } from "sonner";
import { ArrowLeft, ArrowUp, ArrowDown, Upload, Check, X, Plus, Trash2, Music } from "lucide-react";

const FILES = `${process.env.REACT_APP_BACKEND_URL}/api/files/`;

const ASSET_TYPES = [
  ["title_page", "Title Page (A4 portrait JPEG)"],
  ["kannada_summary", "Kannada Summary (A4 portrait JPEG)"],
  ["english_summary", "English Summary (A4 portrait JPEG)"],
  ["color_reference", "Color Reference (JPEG)"],
  ["colouring_page", "Colouring Page (JPEG)"],
];

export default function AdminEComicsVachana({ vachanaId, onBack }) {
  const [data, setData] = useState(null);
  const [uploading, setUploading] = useState("");

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/ecomics/admin/vachanas/${vachanaId}`);
      setData(d);
    } catch (e) { toast.error(formatError(e)); }
  }, [vachanaId]);
  useEffect(() => { load(); }, [load]);

  const uploadAsset = async (assetType, file) => {
    const fd = new FormData();
    fd.append("file", file);
    setUploading(assetType);
    try {
      await api.post(`/ecomics/admin/vachanas/${vachanaId}/asset/${assetType}`, fd, { headers: { "Content-Type": "multipart/form-data" }});
      toast.success("Uploaded");
      load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setUploading(""); }
  };

  const uploadPanelAsset = async (panelId, kind, file) => {
    const fd = new FormData();
    fd.append("file", file);
    setUploading(`${panelId}-${kind}`);
    try {
      await api.post(`/ecomics/admin/panels/${panelId}/asset/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" }});
      toast.success("Uploaded");
      load();
    } catch (e) { toast.error(formatError(e)); }
    finally { setUploading(""); }
  };

  const addPanel = async () => {
    const next = (data?.panels?.length || 0) + 1;
    try {
      await api.post(`/ecomics/admin/vachanas/${vachanaId}/panels`, { panel_number: next });
      toast.success(`Panel ${next} added`);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const archivePanel = async (panelId) => {
    if (!window.confirm("Archive this panel?")) return;
    try { await api.delete(`/ecomics/admin/panels/${panelId}`); toast.success("Panel archived"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const movePanel = async (index, dir) => {
    const other = index + dir;
    if (other < 0 || other >= data.panels.length) return;
    const a = data.panels[index], b = data.panels[other];
    try {
      await api.put("/ecomics/admin/panels/reorder", { items: [
        { id: a.panel_id, display_order: b.display_order },
        { id: b.panel_id, display_order: a.display_order },
      ]});
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const setStatus = async (status) => {
    try {
      await api.post(`/ecomics/admin/vachanas/${vachanaId}/status`, { status });
      toast.success(`Vachana ${status}`);
      load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.missing) toast.error(`Cannot publish. Missing: ${d.missing.join(", ")}`);
      else toast.error(formatError(e));
    }
  };

  if (!data) return <div className="p-10 text-center text-sm text-[var(--bb-muted)]">Loading…</div>;

  const { validation, panels } = data;

  return (
    <div className="space-y-6" data-testid="admin-ecomics-vachana">
      <button onClick={onBack} className="bb-link inline-flex items-center gap-1 text-sm" data-testid="ecomics-back-vachanas"><ArrowLeft size={14}/> Back to vachanas</button>

      <div className="flex flex-wrap items-center gap-4 justify-between">
        <div>
          <div className="bb-overline text-[var(--bb-muted)]">VACHANA {data.sequence_number}</div>
          <h3 className="font-serif text-2xl text-[var(--bb-forest)]">{data.kannada_title}</h3>
          <div className="text-sm text-[var(--bb-muted)]">{data.english_title}</div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`bb-tag ${data.status === "published" ? "bb-tag-green" : ""}`}>{data.status}</span>
          {data.status !== "published"
            ? <button onClick={() => setStatus("published")} className="bb-btn-primary !py-2 !px-4 text-sm" data-testid="ecomics-vachana-publish">Publish</button>
            : <button onClick={() => setStatus("draft")} className="bb-btn-secondary !py-2 !px-4 text-sm" data-testid="ecomics-vachana-unpublish">Unpublish</button>}
        </div>
      </div>

      {/* Validation checklist */}
      <div className="bb-card p-5" data-testid="ecomics-validation">
        <div className="bb-overline text-[var(--bb-muted)] mb-3">UPLOAD STATUS</div>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1.5 text-sm">
          {validation.checklist.map((c) => (
            <div key={c.label} className={`flex items-center gap-2 ${c.ok ? "text-[var(--bb-forest)]" : "text-[var(--bb-error)]"}`} data-testid={`ecomics-check-${c.label.replace(/\s+/g, "-").toLowerCase()}`}>
              {c.ok ? <Check size={14} className="text-green-600 shrink-0"/> : <X size={14} className="shrink-0"/>} {c.label}
            </div>
          ))}
        </div>
        {!validation.can_publish && (
          <p className="mt-3 text-xs text-[var(--bb-error)]" data-testid="ecomics-missing-note">Cannot publish yet — {validation.missing.length} item(s) missing.</p>
        )}
      </div>

      {/* Vachana assets */}
      <div className="bb-card p-5">
        <div className="bb-overline text-[var(--bb-muted)] mb-4">CONTENT ASSETS</div>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {ASSET_TYPES.map(([key, label]) => {
            const path = data.assets?.[key];
            return (
              <div key={key} className="border border-[var(--bb-border)] rounded p-3" data-testid={`ecomics-asset-${key}`}>
                <div className="aspect-[3/4] rounded bg-[var(--bb-sand)] overflow-hidden mb-2 grid place-items-center">
                  {path ? (
                    <a href={FILES + path} target="_blank" rel="noreferrer" className="w-full h-full">
                      <img src={FILES + path} alt={label} loading="lazy" className="w-full h-full object-cover"/>
                    </a>
                  ) : <span className="text-xs text-[var(--bb-muted)]">Not uploaded</span>}
                </div>
                <div className="text-[11px] text-[var(--bb-muted)] leading-tight mb-2">{label}</div>
                <label className="bb-link text-xs cursor-pointer inline-flex items-center gap-1">
                  <Upload size={11}/> {uploading === key ? "Uploading…" : path ? "Replace" : "Upload JPEG"}
                  <input type="file" accept="image/jpeg,image/png" className="hidden" onChange={e => e.target.files[0] && uploadAsset(key, e.target.files[0])} data-testid={`ecomics-asset-input-${key}`}/>
                </label>
              </div>
            );
          })}
        </div>
      </div>

      {/* Panels */}
      <div className="bb-card p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="bb-overline text-[var(--bb-muted)]">COMIC PANELS ({panels.length})</div>
          <button onClick={addPanel} className="bb-btn-secondary !py-1.5 !px-3 text-xs inline-flex items-center gap-1" data-testid="ecomics-add-panel"><Plus size={12}/> Add Panel</button>
        </div>
        {panels.length === 0 ? (
          <div className="p-8 text-center text-sm text-[var(--bb-muted)]" data-testid="ecomics-panels-empty">No panels yet. Add Panel 1.</div>
        ) : (
          <div className="space-y-4">
            {panels.map((p, i) => (
              <div key={p.panel_id} className="border border-[var(--bb-border)] rounded p-4" data-testid={`ecomics-panel-${p.panel_id}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="font-medium text-[var(--bb-forest)]">Panel {p.panel_number} <span className="text-xs text-[var(--bb-muted)] font-normal">· order {p.display_order}</span></div>
                  <div className="flex items-center gap-2 text-xs">
                    <button onClick={() => movePanel(i, -1)} disabled={i === 0} className="p-1.5 rounded border border-[var(--bb-border)] disabled:opacity-30" data-testid={`ecomics-panel-up-${p.panel_id}`}><ArrowUp size={12}/></button>
                    <button onClick={() => movePanel(i, 1)} disabled={i === panels.length - 1} className="p-1.5 rounded border border-[var(--bb-border)] disabled:opacity-30" data-testid={`ecomics-panel-down-${p.panel_id}`}><ArrowDown size={12}/></button>
                    <button onClick={() => archivePanel(p.panel_id)} className="text-[var(--bb-error)] hover:underline inline-flex items-center gap-1" data-testid={`ecomics-panel-archive-${p.panel_id}`}><Trash2 size={12}/> Archive</button>
                  </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-4">
                  {[["kannada_image", "Kannada JPEG"], ["english_image", "English JPEG"]].map(([kind, label]) => (
                    <div key={kind} data-testid={`ecomics-panel-${kind}-${p.panel_id}`}>
                      <div className="aspect-video rounded bg-[var(--bb-sand)] overflow-hidden mb-2 grid place-items-center">
                        {p[kind] ? (
                          <a href={FILES + p[kind]} target="_blank" rel="noreferrer" className="w-full h-full">
                            <img src={FILES + p[kind]} alt={label} loading="lazy" className="w-full h-full object-cover"/>
                          </a>
                        ) : <span className="text-xs text-[var(--bb-muted)]">Not uploaded</span>}
                      </div>
                      <label className="bb-link text-xs cursor-pointer inline-flex items-center gap-1">
                        <Upload size={11}/> {uploading === `${p.panel_id}-${kind}` ? "Uploading…" : `${p[kind] ? "Replace" : "Upload"} ${label}`}
                        <input type="file" accept="image/jpeg,image/png" className="hidden" onChange={e => e.target.files[0] && uploadPanelAsset(p.panel_id, kind, e.target.files[0])} data-testid={`ecomics-panel-input-${kind}-${p.panel_id}`}/>
                      </label>
                    </div>
                  ))}
                  <div data-testid={`ecomics-panel-audio-${p.panel_id}`}>
                    <div className="aspect-video rounded bg-[var(--bb-sand)] mb-2 grid place-items-center p-3">
                      {p.audio ? (
                        <audio controls preload="none" src={FILES + p.audio} className="w-full" data-testid={`ecomics-panel-audio-player-${p.panel_id}`}/>
                      ) : <span className="text-xs text-[var(--bb-muted)] flex items-center gap-1"><Music size={12}/> No audio</span>}
                    </div>
                    <label className="bb-link text-xs cursor-pointer inline-flex items-center gap-1">
                      <Upload size={11}/> {uploading === `${p.panel_id}-audio` ? "Uploading…" : `${p.audio ? "Replace" : "Upload"} Audio (MP3)`}
                      <input type="file" accept="audio/mpeg,audio/mp3,.mp3,audio/*" className="hidden" onChange={e => e.target.files[0] && uploadPanelAsset(p.panel_id, "audio", e.target.files[0])} data-testid={`ecomics-panel-input-audio-${p.panel_id}`}/>
                    </label>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
